import React, {memo} from 'react';
import {View, StyleSheet, ViewStyle} from 'react-native';
import Localized from 'src/constants/AppStrings';
import {checkValue} from 'src/Util';
import Styles from '../../Styles';
import AVText from 'src/components/elements/AVText';
import {NutritionRow} from './components/NutritionRow';

type BlackLineProps = {
  height: number;
};

function BlackLine({height}: BlackLineProps) {
  return <View style={[styles.blackLine, {height}]} />;
}

type NutritionData = {
  productName?: string;
  caloriesFromFat?: number;
  totalFat?: number;
  saturatedFat?: number;
  unsaturatedFat?: number;
  transFat?: number;
  cholesterol?: number;
  sodium?: number;
  totalCarbs?: number;
  dietaryFiber?: number;
  sugars?: number;
  proteing?: number;
  vitaminA?: number;
  vitaminC?: number;
  calcium?: number;
  potassium?: number;
  iron?: number;
  caffeine?: number;
  percentDailyValues?: {
    totalFat?: number;
    saturatedFat?: number;
    cholesterol?: number;
    sodium?: number;
    totalCarbs?: number;
    dietaryFiber?: number;
    sugars?: number;
    protein?: number;
    vitaminA?: number;
    vitaminC?: number;
    calcium?: number;
    potassium?: number;
    iron?: number;
    caffeine?: number;
  };
};

type NutritionFactsDisplayProps = {
  nutrition?: NutritionData;
  product?: any;
  additionalContainerStyles?: ViewStyle;
};

export const NutritionFactsDisplay = memo(
  ({
    nutrition,
    product,
    additionalContainerStyles,
  }: NutritionFactsDisplayProps) => {
    if (!nutrition || Object.keys(nutrition).length === 0) {
      return null;
    }

    return (
      <View style={[styles.container, additionalContainerStyles]}>
        <View style={styles.innerContainer}>
          <AVText
            style={styles.title}
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            accessible
            testID="qa_NutritionFact_txt"
            nativeID="qa_NutritionFact_txt"
          >
            {Localized.Labels.nutrition_facts}
          </AVText>
          <BlackLine height={10} />

          {/* Calories Section */}
          <View style={styles.calorieContainer}>
            <AVText
              style={styles.smallText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            >
              {Localized.Labels.amount_per_serving}
            </AVText>
            <BlackLine height={1} />

            <View style={[styles.fullInformationRow, {marginBottom: 2}]}>
              <AVText
                style={styles.calorieText}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
              >
                {Localized.Labels.calories}
              </AVText>
              <AVText
                style={styles.calorieText}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
              >
                {product?.calories}
              </AVText>
            </View>

            {checkValue(nutrition.caloriesFromFat) && (
              <View
                style={[
                  styles.fullInformationRow,
                  {justifyContent: 'flex-end'},
                ]}
              >
                <AVText
                  style={styles.amtPerServingValue}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                >
                  {Localized.Labels.calories_from_fat}
                </AVText>
                <AVText
                  style={styles.amtPerServingValue}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
                >
                  {nutrition.caloriesFromFat} g
                </AVText>
              </View>
            )}
            <BlackLine height={5} />
          </View>

          {/* Daily Value Header */}
          <View
            style={[
              styles.row,
              {justifyContent: 'flex-end', marginBottom: Styles.Spacing.m1},
            ]}
          >
            <AVText
              style={styles.amtPerServingText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            >
              % {Localized.Labels.daily_value} *
            </AVText>
          </View>

          {/* Fat Section */}
          {checkValue(nutrition.totalFat) && (
            <View style={styles.column}>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.total_fat}
                value={nutrition.totalFat}
                unit="g"
                dailyValue={nutrition.percentDailyValues?.totalFat}
                bold
              />

              {checkValue(nutrition.saturatedFat) && (
                <>
                  <BlackLine height={1} />
                  <NutritionRow
                    label={Localized.Labels.saturated_fat}
                    value={nutrition.saturatedFat}
                    unit="g"
                    dailyValue={nutrition.percentDailyValues?.saturatedFat}
                    indented
                  />
                </>
              )}

              {checkValue(nutrition.unsaturatedFat) && (
                <>
                  <BlackLine height={1} />
                  <NutritionRow
                    label={Localized.Labels.unsaturated_fat}
                    value={nutrition.unsaturatedFat}
                    unit="g"
                    indented
                  />
                </>
              )}

              {checkValue(nutrition.transFat) && (
                <>
                  <BlackLine height={1} />
                  <NutritionRow
                    label={Localized.Labels.trans_fat}
                    value={nutrition.transFat}
                    unit="g"
                    indented
                  />
                </>
              )}
            </View>
          )}

          {/* Cholesterol */}
          {checkValue(nutrition.cholesterol) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.cholesterol}
                value={nutrition.cholesterol}
                unit="mg"
                dailyValue={nutrition.percentDailyValues?.cholesterol}
                bold
              />
            </>
          )}

          {/* Sodium */}
          {checkValue(nutrition.sodium) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.sodium}
                value={nutrition.sodium}
                unit="mg"
                dailyValue={nutrition.percentDailyValues?.sodium}
                bold
              />
            </>
          )}

          {/* Carbohydrates Section */}
          {checkValue(nutrition.totalCarbs) && (
            <View style={styles.column}>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.total_carbohydrate}
                value={nutrition.totalCarbs}
                unit="g"
                dailyValue={nutrition.percentDailyValues?.totalCarbs}
                bold
              />

              {checkValue(nutrition.dietaryFiber) && (
                <>
                  <BlackLine height={1} />
                  <NutritionRow
                    label={Localized.Labels.dietary_fiber}
                    value={nutrition.dietaryFiber}
                    unit="g"
                    dailyValue={nutrition.percentDailyValues?.dietaryFiber}
                    indented
                  />
                </>
              )}

              {checkValue(nutrition.sugars) && (
                <>
                  <BlackLine height={1} />
                  <NutritionRow
                    label={Localized.Labels.sugars}
                    value={nutrition.sugars}
                    unit="g"
                    dailyValue={nutrition.percentDailyValues?.sugars}
                    indented
                  />
                </>
              )}
            </View>
          )}

          {/* Protein */}
          {checkValue(nutrition.proteing) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.protein}
                value={nutrition.proteing}
                unit="g"
                dailyValue={nutrition.percentDailyValues?.protein}
                bold
              />
              <BlackLine height={10} />
            </>
          )}

          {/* Vitamins and Minerals */}
          {checkValue(nutrition.vitaminA) && (
            <NutritionRow
              label={Localized.Labels.vitaminA}
              value={nutrition.vitaminA}
              unit="mcg"
              dailyValue={nutrition.percentDailyValues?.vitaminA}
            />
          )}

          {checkValue(nutrition.vitaminC) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.vitaminC}
                value={nutrition.vitaminC}
                unit="mg"
                dailyValue={nutrition.percentDailyValues?.vitaminC}
              />
            </>
          )}

          {checkValue(nutrition.calcium) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.calcium}
                value={nutrition.calcium}
                unit="mg"
                dailyValue={nutrition.percentDailyValues?.calcium}
              />
            </>
          )}

          {checkValue(nutrition.potassium) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.potassium}
                value={nutrition.potassium}
                unit="mg"
                dailyValue={nutrition.percentDailyValues?.potassium}
              />
            </>
          )}

          {checkValue(nutrition.iron) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.iron}
                value={nutrition.iron}
                unit="mg"
                dailyValue={nutrition.percentDailyValues?.iron}
              />
            </>
          )}

          {checkValue(nutrition.caffeine) && (
            <>
              <BlackLine height={1} />
              <NutritionRow
                label={Localized.Labels.caffeine}
                value={nutrition.caffeine}
                unit="mg"
                dailyValue={nutrition.percentDailyValues?.caffeine}
              />
            </>
          )}

          {/* Footer */}
          <BlackLine height={1} />
          <View style={styles.fullInformationRow}>
            <AVText
              style={styles.bottomText}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
            >
              * {Localized.Labels.percent_daily_values_explaination}
            </AVText>
          </View>
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: Styles.white,
    borderColor: Styles.darkColor,
    borderWidth: 1,
    marginHorizontal: Styles.Spacing.m2,
    marginVertical: Styles.Spacing.m3,
  },
  innerContainer: {
    paddingHorizontal: Styles.Spacing.m2,
    paddingVertical: Styles.Spacing.m1,
  },
  title: {
    fontSize: Styles.Fonts.f3,
    fontWeight: '700',
    color: Styles.darkColor,
    marginBottom: Styles.Spacing.m2,
  },
  blackLine: {
    backgroundColor: Styles.darkColor,
    paddingHorizontal: 20,
  },
  calorieContainer: {
    flexDirection: 'column',
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m2,
  },
  smallText: {
    fontSize: 10,
    marginBottom: 7.5,
  },
  calorieText: {
    paddingTop: Styles.Spacing.m1,
    fontWeight: '700',
    fontSize: 24,
  },
  amtPerServingText: {
    paddingTop: 5,
    fontWeight: '700',
  },
  amtPerServingValue: {
    paddingTop: Styles.Spacing.m1,
    marginHorizontal: Styles.Spacing.m1,
  },
  bottomText: {
    marginTop: Styles.Spacing.m2,
    flexWrap: 'wrap',
    fontSize: 10,
  },
  fullInformationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: Styles.Spacing.m1,
  },
  row: {
    flexDirection: 'row',
    paddingVertical: 2.5,
  },
  column: {
    flexDirection: 'column',
  },
});
